import { isLeft } from 'fp-ts/Either';
import { useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandling';
import { useTRPC } from './useTRPC';

// Simple in-memory cache for presigned URLs
const presignedUrlCache = new Map<string, { url: string; expiresAt: number }>();

const hasPresignedParams = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    for (const key of parsedUrl.searchParams.keys()) {
      if (key.toLowerCase().startsWith('x-amz-')) {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
};

export function useS3Image(s3Url: string | null | undefined) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trpc = useTRPC();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!s3Url) {
      setImageUrl(null);
      return;
    }

    if (hasPresignedParams(s3Url)) {
      setImageUrl(s3Url);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Internal URLs are from our own S3 buckets (dev-portal-uploads-*)
    const normalizedUrl = s3Url.toLowerCase();

    const isDevPortalUploadsBucket =
      normalizedUrl.includes('dev-portal-uploads') ||
      normalizedUrl.includes('developer-portal-uploads');

    const isInternalS3Url =
      isDevPortalUploadsBucket &&
      (normalizedUrl.includes('amazonaws.com') ||
        normalizedUrl.includes('.s3.'));

    // All other URLs (including external S3, Arweave, IPFS, etc) should be used directly
    const isExternalUrl =
      (normalizedUrl.startsWith('http://') ||
        normalizedUrl.startsWith('https://')) &&
      !isInternalS3Url;

    if (isExternalUrl) {
      setImageUrl(s3Url);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cached = presignedUrlCache.get(s3Url);
    if (cached && cached.expiresAt > Date.now()) {
      setImageUrl(cached.url);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchPresignedUrl = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await trpc.getPresignedUrl.query({ url: s3Url });

        if (!abortController.signal.aborted) {
          if ('_tag' in result && isLeft(result)) {
            setError(result.left.message);
            setImageUrl(null);
          } else if ('_tag' in result) {
            const presignedUrl = result.right.presignedUrl;
            setImageUrl(presignedUrl);

            // Cache the URL for 50 minutes (presigned URLs expire in 60 minutes)
            presignedUrlCache.set(s3Url, {
              url: presignedUrl,
              expiresAt: Date.now() + 50 * 60 * 1000,
            });
          }
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          setError(getErrorMessage(err, 'Failed to load image'));
          setImageUrl(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchPresignedUrl();

    return () => {
      abortController.abort();
    };
  }, [s3Url]); // Remove trpc from dependencies

  return { imageUrl, isLoading, error };
}
